// Shared client/server protocol. Imported by both the Worker and web/.

/**
 * Path the app is mounted on: it is served as part of the WorkBrawl hub at
 * https://workbrawl.com/minesbrawl/. Static files live in public/minesbrawl/ to match.
 */
export const BASE_PATH = "/minesbrawl";

/** People per room. PLAYER_COLORS must have at least this many colors. */
export const MAX_PLAYERS = 12;

/** Round lengths the room creator can pick, in seconds. */
export const DURATIONS = [120, 180, 300] as const;
export type Duration = (typeof DURATIONS)[number];
export const DEFAULT_DURATION: Duration = 180;

export const COUNTDOWN_MS = 3000;
/** How long a finished board (cleared or lost) stays on screen before the next one. */
export const REVEAL_MS = 2500;

/** Board sizes. 14 columns is the widest that stays comfortable on a phone. */
export const DIFFICULTIES = {
  easy: { width: 9, height: 9, mines: 10 },
  medium: { width: 12, height: 12, mines: 24 },
  hard: { width: 14, height: 14, mines: 40 },
} as const;
export type Difficulty = keyof typeof DIFFICULTIES;
export const DEFAULT_DIFFICULTY: Difficulty = "medium";

/** Mines the team can set off on one board before it is lost. */
export const LIVES = 3;
/** Points for each safe cell opened (cells that open on their own count for whoever opened them). */
export const CELL_POINTS = 1;
/** Team bonus for clearing a whole board. */
export const CLEAR_BONUS = 10;

/**
 * lobby: waiting for someone to start. countdown: 3-2-1 before a round.
 * running: the team clears boards together while the clock counts down.
 * finished: the team's result is on screen.
 */
export type RoomStatus = "lobby" | "countdown" | "running" | "finished";

// Mid-tone colors that keep contrast on both light and dark backgrounds. Indexed by slot.
export const PLAYER_COLORS = [
  "#e5484d", // red
  "#0b84e5", // blue
  "#2f9e44", // green
  "#f08c00", // orange
  "#8e4ec6", // purple
  "#0f9d9a", // teal
  "#d6409f", // pink
  "#a0703c", // brown
  "#c79a00", // gold
  "#5b5bd6", // indigo
  "#7aa116", // lime
  "#6b7785", // slate
];

export interface PlayerInfo {
  id: string;
  name: string;
  /** 0..MAX_PLAYERS-1: picks the color and marks which cells each person opened. */
  slot: number;
  /** Points this person added to the team in the current (or last) round. */
  score: number;
  online: boolean;
}

/** How the last finished round went for the team. */
export interface RoundSummary {
  /** Team total: everyone's points plus the bonuses for cleared boards. */
  score: number;
  cleared: number;
  lost: number;
  /** Who added what, best first. */
  players: { id: string; name: string; slot: number; score: number }[];
}

export type ClientMsg =
  | { type: "reveal"; cell: number }
  | { type: "flag"; cell: number }
  | { type: "start" }
  | { type: "ping" };

/** JSON body of POST /api/rooms. */
export interface CreateRoomRequest {
  duration?: Duration; // omitted = DEFAULT_DURATION
  difficulty?: Difficulty; // omitted = DEFAULT_DIFFICULTY
  turnstileToken: string;
}

/** JSON body of POST /api/rooms/:code/verify (join by code from the home screen). */
export interface VerifyRoomRequest {
  turnstileToken: string;
}

/** Response of GET /api/config. */
export interface ClientConfig {
  turnstileSiteKey: string;
}

/** Error codes returned as `{ error }` by the HTTP API. */
export type ApiErrorCode = "INVALID_REQUEST" | "TURNSTILE_FAILED" | "ROOM_NOT_FOUND";

/** open: being cleared. cleared / lost: shown for a moment before the next board. */
export type BoardPhase = "open" | "cleared" | "lost";

/** The board on screen, as every client sees it. Cells are x + y * width. */
export interface BoardView {
  /** 1 for the first board of the round, 2 for the second… */
  n: number;
  width: number;
  height: number;
  mines: number;
  /** Mines the team can still set off on this board. */
  lives: number;
  phase: BoardPhase;
  /** Safe cells opened so far: [cell, mines around it, slot of who opened it]. */
  open: [number, number, number][];
  /** Flags: [cell, slot of who placed it]. Anyone on the team can remove them. */
  flags: [number, number][];
  /** Mines that were set off: [cell, slot]. */
  boom: [number, number][];
  /** Where every mine was. Empty while the board is open: positions are never sent early. */
  field: number[];
  /** Team totals for the round so far. */
  score: number;
  cleared: number;
  lost: number;
}

export type ServerMsg =
  | {
      type: "state";
      you: string;
      status: RoomStatus;
      /** Round length in seconds. */
      duration: Duration;
      difficulty: Difficulty;
      /** Milliseconds left in the countdown (status "countdown") or the round ("running"). */
      remainingMs: number;
      players: PlayerInfo[];
      /** Last finished round. Null before the first one ends. */
      summary: RoundSummary | null;
    }
  | ({
      /** Sent whenever something about the board changes. */
      type: "board";
      /** Milliseconds left in the round. */
      t: number;
      /** [slot, points] for every player. */
      p: [number, number][];
    } & BoardView)
  | { type: "player_joined"; player: PlayerInfo }
  | { type: "player_updated"; player: PlayerInfo }
  | { type: "player_left"; id: string }
  | { type: "error"; code: ErrorCode; message: string }
  | { type: "pong" };

/**
 * Exact keep-alive frames. The room registers them as a WebSocket auto-response, so
 * Cloudflare answers pings at the edge WITHOUT waking the Durable Object: an idle room
 * with open tabs stays hibernated (no billable duration). The client must send PING_FRAME
 * byte for byte, or the ping falls through to the room and wakes it.
 */
export const PING_FRAME = '{"type":"ping"}';
export const PONG_FRAME = '{"type":"pong"}';

export type ErrorCode = "ROOM_FULL" | "ROOM_NOT_FOUND" | "BAD_MESSAGE";

export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LENGTH = 5;
export const NAME_MAX_LENGTH = 20;

const ROOM_CODE_RE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidRoomCode(code: string): boolean {
  return ROOM_CODE_RE.test(code);
}

export function isValidPlayerId(id: string): boolean {
  return UUID_RE.test(id);
}

export function isDuration(value: unknown): value is Duration {
  return (DURATIONS as readonly unknown[]).includes(value);
}

export function isDifficulty(value: unknown): value is Difficulty {
  return typeof value === "string" && Object.hasOwn(DIFFICULTIES, value);
}

/**
 * Trims and collapses whitespace. Returns null when the name is empty, longer than
 * NAME_MAX_LENGTH characters, or contains HTML-ish or control characters.
 */
export function sanitizeName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, " ");
  const length = [...name].length;
  if (length < 1 || length > NAME_MAX_LENGTH) return null;
  if (/[<>&"'`\p{Cc}]/u.test(name)) return null;
  return name;
}

/** Best score first; ties keep the lower slot (who joined earlier) ahead. */
export function rank<T extends { score: number; slot: number }>(players: readonly T[]): T[] {
  return [...players].sort((a, b) => b.score - a.score || a.slot - b.slot);
}
